import React from 'react';

import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import AiSparkleIcon from '@atlaskit/icon/core/ai-sparkle';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import CompassIcon from '@atlaskit/icon/core/compass';
import PersonAvatarIcon from '@atlaskit/icon/core/person-avatar';
import StarStarredIcon from '@atlaskit/icon/core/star-starred';
import type { ModalDialogProps } from '@atlaskit/modal-dialog/types';

// eslint-disable-next-line import/extensions
import { defaultAgentResultsEntryPoint } from './entrypoints/default/defaultAgentResults.entrypoint';
import { messages as recommendedMessages } from './entrypoints/recommended/messages';
// eslint-disable-next-line import/extensions
import { recommendedAgentResultsEntryPoint } from './entrypoints/recommended/recommendedAgentResults.entrypoint';
import { messages as templatesMessages } from './entrypoints/templates/messages';
import { templateResultsEntryPoint } from './entrypoints/templates/templateResults.entrypoint';
import { yourAgentResultsEntryPoint } from './entrypoints/your-agents/yourAgentResults.entrypoint';
import { messages } from './messages';
import type { BrowseAgentCategory, CategoryEntryPoint, SkeletonProps } from './types';
import { RecommendedSkeleton } from './ui/RecommendedSkeleton/RecommendedSkeleton';
import { YourAgentsSkeleton } from './ui/YourAgentsSkeleton/YourAgentsSkeleton';
import { createCardListSkeleton } from './utils/createCardListSkeleton';

export const ALL_AGENTS_CATEGORY_ID = 'all-agents';
export const RECOMMENDED_CATEGORY_ID = 'recommended';
export const STARRED_AGENTS_CATEGORY_ID = 'starred';
export const VERIFIED_AGENTS_CATEGORY_ID = 'verified';
export const YOUR_AGENTS_CATEGORY_ID = 'your-agents';
export const TEMPLATES_CATEGORY_ID = 'templates';

export const browseAgentModalProps: Pick<ModalDialogProps, 'height' | 'width'> = {
	height: '80vh',
	width: '1020px',
};

export const DEFAULT_CATEGORIES: readonly BrowseAgentCategory[] = [
	{
		id: RECOMMENDED_CATEGORY_ID,
		name: recommendedMessages.recommendedAgentsCategory,
		entrypoint: recommendedAgentResultsEntryPoint as CategoryEntryPoint,
		icon: AiSparkleIcon,
		skeleton: ({ testId }: SkeletonProps): React.JSX.Element => (
			<RecommendedSkeleton testId={testId} />
		),
	},
	{
		id: ALL_AGENTS_CATEGORY_ID,
		name: messages.allAgentsCategory,
		get entrypoint() {
			return defaultAgentResultsEntryPoint;
		},
		icon: AiAgentIcon,
		skeleton: createCardListSkeleton({ count: 6 }),
	},
	{
		id: STARRED_AGENTS_CATEGORY_ID,
		name: messages.starredCategory,
		get entrypoint() {
			return defaultAgentResultsEntryPoint;
		},
		icon: StarStarredIcon,
		skeleton: createCardListSkeleton({ count: 6 }),
	},
	{
		id: YOUR_AGENTS_CATEGORY_ID,
		name: messages.yourAgentsCategory,
		get entrypoint() {
			return defaultAgentResultsEntryPoint;
		},
		icon: PersonAvatarIcon,
		skeleton: createCardListSkeleton({ count: 6 }),
	},
	{
		id: VERIFIED_AGENTS_CATEGORY_ID,
		name: messages.verifiedCategory,
		get entrypoint() {
			return defaultAgentResultsEntryPoint;
		},
		icon: CheckCircleIcon,
		skeleton: createCardListSkeleton({ count: 6 }),
	},
];

export const UPLIFT_DEFAULT_CATEGORIES: readonly BrowseAgentCategory[] = [
	{
		id: RECOMMENDED_CATEGORY_ID,
		name: recommendedMessages.recommendedAgentsCategory,
		entrypoint: recommendedAgentResultsEntryPoint as CategoryEntryPoint,
		icon: AiSparkleIcon,
		skeleton: RecommendedSkeleton,
	},
	{
		id: YOUR_AGENTS_CATEGORY_ID,
		name: messages.yourAgentsCategory,
		entrypoint: yourAgentResultsEntryPoint,
		icon: PersonAvatarIcon,
		skeleton: YourAgentsSkeleton,
	},
	{
		id: VERIFIED_AGENTS_CATEGORY_ID,
		name: messages.verifiedCategory,
		get entrypoint() {
			return defaultAgentResultsEntryPoint;
		},
		icon: CheckCircleIcon,
		skeleton: createCardListSkeleton({ count: 6 }),
	},
	{
		id: TEMPLATES_CATEGORY_ID,
		name: templatesMessages.templatesCategory,
		entrypoint: templateResultsEntryPoint as CategoryEntryPoint,
		icon: CompassIcon,
		skeleton: createCardListSkeleton({ count: 12, showHeading: true }),
	},
	{
		id: ALL_AGENTS_CATEGORY_ID,
		name: messages.allAgentsCategory,
		get entrypoint() {
			return defaultAgentResultsEntryPoint;
		},
		icon: AiAgentIcon,
		skeleton: createCardListSkeleton({ count: 6 }),
	},
];
